import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";

/**
 * Captura un ticket con la webcam del ordenador (getUserMedia).
 * En móvil conviene seguir usando <input capture>.
 */
const ExpenseUserWebcamDialog = ({ open, onClose, onCapture, t }) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setReady(false);
    setError(null);

    const start = async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        setError(
          t(
            "ExpenseUser.webcam.unsupported",
            "Este navegador no permite usar la cámara.",
          ),
        );
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: "user",
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        const video = videoRef.current;
        if (video) {
          video.srcObject = stream;
          await video.play();
        }
        setReady(true);
      } catch (e) {
        if (!cancelled) {
          setError(
            e?.message ||
              t(
                "ExpenseUser.webcam.error",
                "No se pudo abrir la cámara. Revisa permisos del navegador.",
              ),
          );
        }
      }
    };

    start();

    return () => {
      cancelled = true;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
      const video = videoRef.current;
      if (video) video.srcObject = null;
    };
  }, [open, t]);

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    const video = videoRef.current;
    if (video) video.srcObject = null;
  };

  const handleClose = () => {
    if (busy) return;
    stopStream();
    onClose?.();
  };

  const handleSnap = async () => {
    const video = videoRef.current;
    if (!video || !ready) return;
    try {
      setBusy(true);
      const canvas = document.createElement("canvas");
      const w = video.videoWidth || 1280;
      const h = video.videoHeight || 720;
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(video, 0, 0, w, h);
      const blob = await new Promise((resolve, reject) => {
        canvas.toBlob(
          (b) => (b ? resolve(b) : reject(new Error("No se pudo capturar"))),
          "image/jpeg",
          0.9,
        );
      });
      const file = new File([blob], `ticket-${Date.now()}.jpg`, {
        type: "image/jpeg",
      });
      stopStream();
      await onCapture?.(file);
      onClose?.();
    } catch (e) {
      setError(
        e?.message ||
          t("ExpenseUser.webcam.error", "No se pudo hacer la foto."),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle>
        {t("ExpenseUser.webcam.title", "Hacer foto con la cámara")}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            {error}
          </Alert>
        )}
        <Box
          sx={{
            position: "relative",
            bgcolor: "black",
            borderRadius: 2,
            overflow: "hidden",
            minHeight: 240,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {!ready && !error && <CircularProgress size={32} sx={{ color: "white" }} />}
          <video
            ref={videoRef}
            playsInline
            muted
            autoPlay
            style={{
              width: "100%",
              maxHeight: 420,
              display: ready ? "block" : "none",
              objectFit: "cover",
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2, gap: 1 }}>
        <Button onClick={handleClose} disabled={busy} variant="outlined">
          {t("ExpenseUser.cancel", "Cancelar")}
        </Button>
        <Button
          onClick={handleSnap}
          disabled={!ready || busy}
          variant="contained"
        >
          {busy
            ? t("ExpenseUser.webcam.saving", "Guardando…")
            : t("ExpenseUser.webcam.snap", "Capturar")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExpenseUserWebcamDialog;
